/**
 * Per-person pricing by number of concerts attended.
 *
 * Always pass the active festival's pricing (see getFestivalPricing). There
 * is no built-in fallback price: a festival without its base price is not
 * priced yet (`null`), and nothing may be charged or shown as owed for it.
 */
export type FestivalPricingInput = {
	pricingModel?: string | null
	priceOne?: number | null
	priceTwo?: number | null
	priceThree?: number | null
	priceFour?: number | null
	pricePerConcert?: number | null
	festivalPassPrice?: number | null
	childPrice?: number | null
}

export function getFestivalPricing(festival: FestivalPricingInput | null) {
	return festival ?? {}
}

/**
 * Whether the festival has its prices: the per-concert price (SIMPLE) or the
 * one-concert tier (TIERED). PER_CONCERT festivals price each concert
 * instead — see `hasNoPrices` for those.
 */
export function isFestivalPriced(
	pricing: FestivalPricingInput | null | undefined,
): boolean {
	if (!pricing) return false
	if (pricing.pricingModel === 'PER_CONCERT') return true
	if (pricing.pricingModel === 'SIMPLE') return pricing.pricePerConcert != null
	return pricing.priceOne != null
}

/**
 * What one person pays for `ticketCount` concerts, or `null` while the
 * festival has no prices. Children pay the flat child price (unset = free).
 *
 * A PER_CONCERT festival has no person price — each ticket carries its own
 * (`ticketPrice`, ADR 0002) — so it is always `null` here: a caller on the
 * legacy path shows "no price" instead of a made-up 0 that reads as paid.
 */
export function calculatePrice(
	ticketCount: number,
	ticketType: 'adult' | 'child',
	pricing: FestivalPricingInput = {},
): number | null {
	if (pricing.pricingModel === 'PER_CONCERT') return null
	if (!isFestivalPriced(pricing)) return null
	if (ticketType === 'child') {
		return pricing.childPrice ?? 0
	}
	if (ticketCount <= 0) return 0

	if (pricing.pricingModel === 'SIMPLE') {
		let total = ticketCount * (pricing.pricePerConcert ?? 0)
		if (pricing.festivalPassPrice) {
			total = Math.min(total, pricing.festivalPassPrice)
		}
		return total
	}

	const priceOne = pricing.priceOne ?? 0
	const tiers = [
		priceOne,
		pricing.priceTwo,
		pricing.priceThree,
		pricing.priceFour,
	]
	// "4" is the price for four or more; a tier left unset gives no discount
	return tiers[Math.min(ticketCount, 4) - 1] ?? priceOne * ticketCount
}

export type PriceBreakdown = {
	/** Concerts in the set */
	concertCount: number
	/** What ONE concert would cost alone (children: flat child price) */
	unitPrice: number
	/** unitPrice × concertCount — the plain sum, shown struck-through */
	listTotal: number
	/** The actual festival price (stepped TIERED / pass-capped SIMPLE) */
	festivalPrice: number
	/** listTotal − festivalPrice, never negative */
	savings: number
}

/**
 * Explains the per-person festival price against plain per-concert pricing:
 * adults would pay `priceOne` per concert bought alone, children carry their
 * flat child price per concert. The difference is what the festival pricing
 * (stepped tier or pass cap) saves them.
 */
export function priceBreakdown(
	ticketCount: number,
	ticketType: 'adult' | 'child',
	pricing: FestivalPricingInput = {},
): PriceBreakdown | null {
	const festivalPrice = calculatePrice(ticketCount, ticketType, pricing)
	const unitPrice = calculatePrice(1, ticketType, pricing)
	if (festivalPrice == null || unitPrice == null) return null
	const listTotal = unitPrice * Math.max(0, ticketCount)
	return {
		concertCount: Math.max(0, ticketCount),
		unitPrice,
		listTotal,
		festivalPrice,
		savings: Math.max(0, Math.round((listTotal - festivalPrice) * 100) / 100),
	}
}
